import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { openHireAgreementPrint } from '@/components/pmv/PrintableHireAgreement';
import api from '@/services/api.service';
import pmvService, { type PmvProfileListItem } from '@/services/pmv.service';
import pmvHireService, {
  formatHireMoney,
  type PmvHireAgreement,
  type PmvHireRate,
} from '@/services/pmv-hire.service';
import { purchaseOrderService, type PurchaseOrderItemDTO } from '@/services/purchaseOrder.service';
import { currencyService } from '@/services/currency.service';
import { epcService } from '@/services/epc.service';
import { useProjects } from '@/hooks/useProjects';
import { formatProjectOption } from '@/utils/pmvDisplay';

const { Text, Paragraph } = Typography;

const OWNERSHIP = [
  { value: 'RENTED', label: 'Rented (Hired)' },
  { value: 'LEASED', label: 'Leased' },
  { value: 'OWNED', label: 'Owned (internal commercial atypical)' },
  { value: 'CLIENT_PROVIDED', label: 'Client provided' },
  { value: 'SUBCONTRACTOR_PROVIDED', label: 'Subcontractor provided' },
];

const RESP_PARTY = [
  { value: 'CONTRACTOR', label: 'Contractor (us)' },
  { value: 'VENDOR', label: 'Vendor (supplier)' },
  { value: 'SHARED', label: 'Shared' },
  { value: 'OTHER', label: 'Other' },
];

const MOB_DEMOB = [
  { value: 'CONTRACTOR', label: 'Contractor' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'INCLUDED', label: 'Included in hire' },
  { value: 'FIXED_CHARGE', label: 'Fixed charge (commercial)' },
  { value: 'PO_LINKED', label: 'PO / Procurement linked' },
  { value: 'NOT_APPLICABLE', label: 'Not applicable' },
  { value: 'MANUAL_REVIEW', label: 'Manual commercial review' },
];

const RATE_BASIS = [
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'SHIFT', label: 'Shift' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'LUMP_SUM', label: 'Lump sum' },
  { value: 'MIXED', label: 'Mixed / scheduled' },
];

function unwrapList<T>(res: unknown): T[] {
  const body = (res as { data?: unknown })?.data ?? res;
  const inner = (body as { data?: unknown })?.data ?? body;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

function profileLabel(p: PmvProfileListItem): string {
  const fleet = p.fleet_number ? `${p.fleet_number} · ` : '';
  const own = p.ownership_type ? ` [${p.ownership_type}]` : '';
  return `${fleet}${p.asset_code} — ${p.asset_name || 'Equipment'}${own}`;
}

function poLineLabel(line: PurchaseOrderItemDTO): string {
  const code = line.item_code ? `${line.item_code} · ` : '';
  return `Line ${line.line_number}: ${code}${line.item_description || 'Item'} @ ${line.unit_price}`;
}

/** Agreements list + enterprise detail/form. */
const PmvHireAgreementPage: React.FC = () => {
  const { id } = useParams();
  const isDetail = !!id && id !== 'new';
  const isNew = id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>();
  const [rateOpen, setRateOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [form] = Form.useForm();
  const [rateForm] = Form.useForm();

  const watchedSupplierId = Form.useWatch('supplier_id', form);
  const watchedProjectId = Form.useWatch('project_id', form);
  const watchedPoId = Form.useWatch('purchase_order_id', form);
  const watchedProfileId = Form.useWatch('pmv_profile_id', form);
  const watchedOwnership = Form.useWatch('ownership_type', form);

  const { data: projects = [] } = useProjects();

  const listQ = useQuery({
    queryKey: ['pmv-hire-agreements', status],
    queryFn: () => pmvHireService.listAgreements({ status, limit: 200 }),
    enabled: !isDetail && !isNew,
  });

  const detailQ = useQuery({
    queryKey: ['pmv-hire-agreement', id],
    queryFn: () => pmvHireService.getAgreement(id!),
    enabled: isDetail,
  });

  const ratesQ = useQuery({
    queryKey: ['pmv-hire-rates', id],
    queryFn: async () => {
      const data = await pmvHireService.listRates(id!);
      return Array.isArray(data) ? data : data.items ?? [];
    },
    enabled: isDetail,
  });

  const effectiveQ = useQuery({
    queryKey: ['pmv-hire-rate-effective', id],
    queryFn: () => pmvHireService.resolveEffectiveRate(id!),
    enabled: isDetail,
  });

  const suppliersQ = useQuery({
    queryKey: ['pmv-hire-suppliers', supplierSearch],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers', {
        params: supplierSearch ? { search: supplierSearch } : undefined,
      });
      return unwrapList<{ id: string; supplier_code: string; supplier_name: string; is_active?: boolean }>(
        res
      );
    },
    staleTime: 30_000,
  });

  const profilesQ = useQuery({
    queryKey: ['pmv-hire-profiles', equipmentSearch],
    queryFn: () =>
      pmvService.listProfiles({
        page: 1,
        pageSize: 200,
        q: equipmentSearch || undefined,
      }),
    staleTime: 30_000,
  });

  const currenciesQ = useQuery({
    queryKey: ['finance-currencies'],
    queryFn: () => currencyService.getCurrencies(),
    staleTime: 60_000,
  });

  const posQ = useQuery({
    queryKey: ['pmv-hire-pos', watchedSupplierId, watchedProjectId],
    queryFn: () =>
      purchaseOrderService.list({
        supplier_id: watchedSupplierId || undefined,
        project_id: watchedProjectId || undefined,
      }),
    enabled: !!watchedSupplierId,
    staleTime: 20_000,
  });

  const poItemsQ = useQuery({
    queryKey: ['pmv-hire-po-items', watchedPoId],
    queryFn: () => purchaseOrderService.getItems(watchedPoId!),
    enabled: !!watchedPoId,
  });

  const workFrontsQ = useQuery({
    queryKey: ['cep-work-fronts', watchedProjectId],
    queryFn: () => epcService.getWorkFronts(watchedProjectId!),
    enabled: !!watchedProjectId,
    staleTime: 30_000,
  });

  const selectedProfile = useMemo(
    () => (profilesQ.data?.data ?? []).find((p) => p.id === watchedProfileId),
    [profilesQ.data, watchedProfileId]
  );

  const supplierOptions = useMemo(
    () =>
      (suppliersQ.data ?? [])
        .filter((s) => s.is_active !== false)
        .map((s) => ({
          value: s.id,
          label: `${s.supplier_code} — ${s.supplier_name}`,
        })),
    [suppliersQ.data]
  );

  const equipmentOptions = useMemo(() => {
    const rows = profilesQ.data?.data ?? [];
    const preferred = rows.filter((p) =>
      ['RENTED', 'LEASED', 'CLIENT_PROVIDED', 'SUBCONTRACTOR_PROVIDED'].includes(p.ownership_type)
    );
    const rest = rows.filter((p) => !preferred.includes(p));
    return [...preferred, ...rest].map((p) => ({
      value: p.id,
      label: profileLabel(p),
    }));
  }, [profilesQ.data]);

  const currencyOptions = useMemo(() => {
    const rows = currenciesQ.data ?? [];
    if (!rows.length) {
      return [
        { value: 'AED', label: 'AED' },
        { value: 'USD', label: 'USD' },
        { value: 'EUR', label: 'EUR' },
      ];
    }
    return rows
      .filter((c) => c.is_active !== false)
      .map((c) => {
        const code = c.currency_code || c.code || '';
        return {
          value: code,
          label: `${code}${c.currency_name ? ` — ${c.currency_name}` : ''}${
            c.is_base_currency ? ' (base)' : ''
          }`,
        };
      })
      .filter((o) => o.value);
  }, [currenciesQ.data]);

  const poOptions = useMemo(
    () =>
      (posQ.data ?? []).map((po) => ({
        value: po.id,
        label: `${po.po_number} · ${po.po_status || ''} · ${po.po_type || 'PO'}`,
      })),
    [posQ.data]
  );

  const poLineOptions = useMemo(
    () =>
      (poItemsQ.data ?? []).map((line) => ({
        value: line.id,
        label: poLineLabel(line),
      })),
    [poItemsQ.data]
  );

  const workFrontOptions = useMemo(() => {
    const rows = (workFrontsQ.data ?? []) as Array<{
      id?: string;
      name?: string;
      code?: string;
      title?: string;
    }>;
    return rows
      .filter((w) => w.id)
      .map((w) => ({
        value: w.id!,
        label: w.code || w.name || w.title || w.id!,
      }));
  }, [workFrontsQ.data]);

  const projectLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, formatProjectOption(p));
    return m;
  }, [projects]);

  const supplierLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of suppliersQ.data ?? []) {
      m.set(s.id, `${s.supplier_code} — ${s.supplier_name}`);
    }
    return m;
  }, [suppliersQ.data]);

  useEffect(() => {
    if (!detailQ.data) return;
    const a = detailQ.data;
    form.setFieldsValue({
      ...a,
      planned_on_hire_date: a.planned_on_hire_date ? dayjs(a.planned_on_hire_date) : null,
      planned_off_hire_date: a.planned_off_hire_date ? dayjs(a.planned_off_hire_date) : null,
    });
  }, [detailQ.data, form]);

  // When equipment selected on create, default ownership from profile if still RENTED default
  useEffect(() => {
    if (!isNew || !selectedProfile) return;
    const current = form.getFieldValue('ownership_type');
    if (!current || current === 'RENTED') {
      if (
        ['RENTED', 'LEASED', 'CLIENT_PROVIDED', 'SUBCONTRACTOR_PROVIDED', 'OWNED'].includes(
          selectedProfile.ownership_type
        )
      ) {
        form.setFieldsValue({ ownership_type: selectedProfile.ownership_type });
      }
    }
    if (!form.getFieldValue('project_id') && selectedProfile.current_project_id) {
      form.setFieldsValue({ project_id: selectedProfile.current_project_id });
    }
  }, [selectedProfile, isNew, form]);

  // Clear PO line when PO changes
  useEffect(() => {
    if (!watchedPoId) {
      form.setFieldsValue({ purchase_order_line_id: undefined });
    }
  }, [watchedPoId, form]);

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvHireService.createAgreement(body),
    onSuccess: (a) => {
      message.success(`Created ${a.agreement_number}`);
      navigate(`/asset/pmv/hire/agreements/${a.id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const patchMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvHireService.patchAgreement(id!, body),
    onSuccess: () => {
      message.success('Agreement saved');
      qc.invalidateQueries({ queryKey: ['pmv-hire-agreement', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const rateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvHireService.createRate(id!, body),
    onSuccess: () => {
      message.success('Rate schedule added (effective-dated; does not rewrite history)');
      setRateOpen(false);
      rateForm.resetFields();
      qc.invalidateQueries({ queryKey: ['pmv-hire-rates', id] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-rate-effective', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Rate create failed'),
  });

  const effective = effectiveQ.data;
  const effectiveRate =
    effective && typeof effective === 'object' && 'rate' in effective
      ? (effective as { rate: PmvHireRate | null; available?: boolean }).rate
      : (effective as PmvHireRate | null);
  const rateAvailable =
    effective && typeof effective === 'object' && 'available' in effective
      ? !!(effective as { available?: boolean }).available
      : !!effectiveRate;

  const currentRateDisplay = (() => {
    if (!rateAvailable || !effectiveRate) return 'Unavailable';
    const basis = effectiveRate.rate_basis;
    const raw =
      basis === 'HOURLY'
        ? effectiveRate.hourly_rate
        : basis === 'DAILY'
          ? effectiveRate.daily_rate
          : effectiveRate.monthly_rate;
    if (raw == null || raw === '') return 'Unavailable';
    return `${formatHireMoney(raw, effectiveRate.currency)} / ${basis}`;
  })();

  const submitAgreement = (v: Record<string, unknown>) => {
    const body = {
      ...v,
      planned_on_hire_date: v.planned_on_hire_date
        ? dayjs(v.planned_on_hire_date as string).format('YYYY-MM-DD')
        : null,
      planned_off_hire_date: v.planned_off_hire_date
        ? dayjs(v.planned_off_hire_date as string).format('YYYY-MM-DD')
        : null,
      purchase_order_id: v.purchase_order_id || null,
      purchase_order_line_id: v.purchase_order_line_id || null,
      project_id: v.project_id || null,
      work_front_id: v.work_front_id || null,
      site_reference: (v.site_reference as string)?.trim() || null,
      contract_reference: (v.contract_reference as string)?.trim() || null,
      tax_treatment_ref: (v.tax_treatment_ref as string)?.trim() || null,
      notes: (v.notes as string)?.trim() || null,
    };
    if (isNew) createMut.mutate(body);
    else patchMut.mutate(body);
  };

  const handlePrint = async () => {
    const a = detailQ.data;
    if (!a) {
      message.warning('Load the agreement before printing');
      return;
    }
    setPrinting(true);
    try {
      let supplierLabel =
        supplierLabelById.get(a.supplier_id) ||
        supplierOptions.find((o) => o.value === a.supplier_id)?.label;
      if (!supplierLabel && a.supplier_id) {
        try {
          const res = await api.get(`/procurement/suppliers/${a.supplier_id}`);
          const body = (res as { data?: unknown })?.data ?? res;
          const row =
            (body as { data?: { supplier_code?: string; supplier_name?: string } })?.data ?? body;
          if (row && typeof row === 'object' && 'supplier_name' in (row as object)) {
            const s = row as { supplier_code?: string; supplier_name?: string };
            supplierLabel = `${s.supplier_code || ''} — ${s.supplier_name || ''}`.replace(
              /^ — | — $/g,
              ''
            );
          }
        } catch {
          supplierLabel = a.supplier_id;
        }
      }

      let equipmentLabel =
        selectedProfile
          ? profileLabel(selectedProfile)
          : equipmentOptions.find((o) => o.value === a.pmv_profile_id)?.label;
      if (!equipmentLabel && a.pmv_profile_id) {
        try {
          const p = await pmvService.getProfile(a.pmv_profile_id);
          equipmentLabel = profileLabel(p as PmvProfileListItem);
        } catch {
          equipmentLabel = a.pmv_profile_id;
        }
      }

      let poLabel = poOptions.find((o) => o.value === a.purchase_order_id)?.label;
      let poLineLbl = poLineOptions.find((o) => o.value === a.purchase_order_line_id)?.label;
      if (a.purchase_order_id && !poLabel) {
        try {
          const po = await purchaseOrderService.getById(a.purchase_order_id);
          poLabel = `${po.po_number} · ${po.po_status || ''} · ${po.supplier_name || ''}`.trim();
          if (a.purchase_order_line_id) {
            const items = await purchaseOrderService.getItems(a.purchase_order_id);
            const line = items.find((l) => l.id === a.purchase_order_line_id);
            if (line) poLineLbl = poLineLabel(line);
          }
        } catch {
          poLabel = a.purchase_order_id;
        }
      }

      const projectLabel = a.project_id
        ? projectLabelById.get(a.project_id) || a.project_id
        : undefined;
      const wfLabel = a.work_front_id
        ? workFrontOptions.find((o) => o.value === a.work_front_id)?.label || a.work_front_id
        : undefined;

      openHireAgreementPrint({
        agreement: a,
        rates: ratesQ.data ?? [],
        supplierLabel: supplierLabel || undefined,
        equipmentLabel: equipmentLabel || undefined,
        projectLabel,
        poLabel: poLabel || undefined,
        poLineLabel: poLineLbl || undefined,
        workFrontLabel: wfLabel,
        effectiveRateLabel: currentRateDisplay !== 'Unavailable' ? currentRateDisplay : undefined,
      });
    } catch (e) {
      message.error((e as Error).message || 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  if (isNew || isDetail) {
    const a = detailQ.data;
    return (
      <WorkspaceLayout>
        <EnterprisePageHeader
          documentLabel={isNew ? 'New Hire Agreement' : 'Hire Agreement'}
          documentNumber={a?.agreement_number}
          subtitle={
            <Text type="secondary">
              Commercial profile for rented/leased equipment — references Procurement Vendor &amp; PO.
              Does not replace PO, Invoice, AP or GL.
            </Text>
          }
          statusTag={a ? <Tag color="blue">{a.commercial_status}</Tag> : <Tag>DRAFT</Tag>}
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/hire/agreements')}>Register</Button>
              {isDetail ? (
                <>
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    loading={printing}
                    disabled={!a}
                    onClick={() => void handlePrint()}
                  >
                    Print
                  </Button>
                  <Button onClick={() => navigate(`/asset/pmv/hire/on-off?agreement=${id}`)}>
                    On / Off-Hire
                  </Button>
                  <Button onClick={() => navigate(`/asset/pmv/${a?.pmv_profile_id}?tab=hire`)}>
                    Equipment Passport
                  </Button>
                  <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
                </>
              ) : null}
            </Space>
          }
        />

        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Authority boundary"
          description="Vendor master, Purchase Order and Supplier Invoice remain Procurement / AP authority. This agreement interprets verified Daily Logs against contractual hire terms only."
        />

        {selectedProfile && isNew ? (
          <Alert
            style={{ marginBottom: 12 }}
            type="success"
            showIcon
            message={`Selected equipment: ${profileLabel(selectedProfile)}`}
            description={`Operational status ${selectedProfile.operational_status}. Ownership on profile: ${selectedProfile.ownership_type}.`}
          />
        ) : null}

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            ownership_type: 'RENTED',
            currency: 'AED',
            operator_responsibility: 'CONTRACTOR',
            fuel_responsibility: 'CONTRACTOR',
            maintenance_responsibility: 'VENDOR',
            tyre_responsibility: 'VENDOR',
            mob_responsibility: 'VENDOR',
            demob_responsibility: 'VENDOR',
          }}
          onFinish={submitAgreement}
        >
          <Tabs
            items={[
              {
                key: 'parties',
                label: 'Parties & equipment',
                children: (
                  <Card size="small">
                    <Row gutter={16}>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          name="pmv_profile_id"
                          label="Equipment (PMV / Asset)"
                          rules={[{ required: true, message: 'Select equipment' }]}
                          extra="Hired units retain Asset + PMV identity. Prefer RENTED / LEASED ownership."
                        >
                          <Select
                            showSearch
                            disabled={isDetail}
                            placeholder="Search fleet number, asset code…"
                            options={equipmentOptions}
                            filterOption={false}
                            onSearch={setEquipmentSearch}
                            loading={profilesQ.isLoading}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          name="supplier_id"
                          label="Supplier (Vendor)"
                          rules={[{ required: true, message: 'Select supplier' }]}
                          extra={
                            <span>
                              From Procurement supplier master.{' '}
                              <Link to="/scm/procurement/suppliers" target="_blank">
                                Open suppliers
                              </Link>
                            </span>
                          }
                        >
                          <Select
                            showSearch
                            placeholder="Search supplier code or name…"
                            options={supplierOptions}
                            filterOption={false}
                            onSearch={setSupplierSearch}
                            loading={suppliersQ.isLoading}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="ownership_type"
                          label="Commercial ownership"
                          rules={[{ required: true }]}
                          extra="Product “Hired” maps to RENTED or LEASED. HIRED is not a valid enum."
                        >
                          <Select options={OWNERSHIP} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="currency" label="Currency" rules={[{ required: true }]}>
                          <Select
                            showSearch
                            options={currencyOptions}
                            optionFilterProp="label"
                            loading={currenciesQ.isLoading}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name="contract_reference"
                          label="Agreement / contract reference"
                          extra="Vendor hire contract number, framework agreement, or rental call-off reference."
                        >
                          <Input placeholder="e.g. RNT-ABC-2026-014" maxLength={120} />
                        </Form.Item>
                      </Col>
                    </Row>
                    {watchedOwnership === 'OWNED' ? (
                      <Alert
                        type="warning"
                        showIcon
                        message="OWNED equipment normally uses internal hire rates, not vendor hire agreements."
                      />
                    ) : null}
                  </Card>
                ),
              },
              {
                key: 'procurement',
                label: 'Procurement linkage',
                children: (
                  <Card size="small">
                    <Paragraph type="secondary" style={{ marginTop: 0 }}>
                      Link the Purchase Order / Service PO that authorises commercial spend. PMV does
                      not create POs.
                    </Paragraph>
                    {!watchedSupplierId ? (
                      <Alert
                        type="warning"
                        showIcon
                        message="Select a supplier first to load related purchase orders."
                        style={{ marginBottom: 12 }}
                      />
                    ) : null}
                    <Row gutter={16}>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          name="purchase_order_id"
                          label="Purchase Order"
                          extra={
                            watchedSupplierId ? (
                              <Link
                                to={`/scm/procurement/purchase-orders?supplier_id=${watchedSupplierId}`}
                                target="_blank"
                              >
                                Browse POs for this supplier
                              </Link>
                            ) : (
                              'Supplier required'
                            )
                          }
                        >
                          <Select
                            allowClear
                            showSearch
                            disabled={!watchedSupplierId}
                            placeholder={
                              watchedSupplierId ? 'Select PO…' : 'Select supplier first'
                            }
                            options={poOptions}
                            loading={posQ.isLoading}
                            optionFilterProp="label"
                            onChange={() =>
                              form.setFieldsValue({ purchase_order_line_id: undefined })
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          name="purchase_order_line_id"
                          label="PO line"
                          extra="Prefer equipment / service line — avoid fuzzy description matching."
                        >
                          <Select
                            allowClear
                            showSearch
                            disabled={!watchedPoId}
                            placeholder={watchedPoId ? 'Select PO line…' : 'Select PO first'}
                            options={poLineOptions}
                            loading={poItemsQ.isLoading}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="tax_treatment_ref"
                          label="Tax / VAT treatment reference"
                          extra="Reference only — Finance owns tax posting."
                        >
                          <Input placeholder="e.g. STANDARD_VAT / ZERO_RATED" maxLength={80} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ),
              },
              {
                key: 'site',
                label: 'Project, site & schedule',
                children: (
                  <Card size="small">
                    <Row gutter={16}>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          name="project_id"
                          label="Project"
                          extra="Project-scoped commercial visibility."
                        >
                          <ProjectSelector
                            value={watchedProjectId || undefined}
                            allowClear
                            placeholder="Select project"
                            onChange={(projectId) => {
                              form.setFieldsValue({
                                project_id: projectId,
                                work_front_id: undefined,
                              });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          name="work_front_id"
                          label="Work front"
                          extra="From EPC work fronts for the selected project (optional)."
                        >
                          <Select
                            allowClear
                            showSearch
                            disabled={!watchedProjectId}
                            placeholder={
                              watchedProjectId ? 'Select work front…' : 'Select project first'
                            }
                            options={workFrontOptions}
                            loading={workFrontsQ.isLoading}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          name="site_reference"
                          label="Site / area reference"
                          extra="Free-text site label (no shadow site master)."
                        >
                          <Input placeholder="e.g. Area 4 — Trenching Zone B" maxLength={120} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Form.Item name="planned_on_hire_date" label="Planned on-hire">
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Form.Item name="planned_off_hire_date" label="Planned off-hire">
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    {isDetail && a ? (
                      <>
                        <Divider orientation="left" plain>
                          Commercial timeline (read-only)
                        </Divider>
                        <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                          <Descriptions.Item label="Status">
                            <Tag>{a.commercial_status}</Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Commercial on-hire">
                            {a.commercial_on_hire_at
                              ? dayjs(a.commercial_on_hire_at).format('YYYY-MM-DD HH:mm')
                              : '—'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Off-hire requested">
                            {a.commercial_off_hire_requested_at
                              ? dayjs(a.commercial_off_hire_requested_at).format('YYYY-MM-DD HH:mm')
                              : '—'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Off-hire effective">
                            {a.commercial_off_hire_effective_at
                              ? dayjs(a.commercial_off_hire_effective_at).format('YYYY-MM-DD HH:mm')
                              : '—'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Physical mobilize">
                            {a.physical_mobilize_at
                              ? dayjs(a.physical_mobilize_at).format('YYYY-MM-DD HH:mm')
                              : '—'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Physical demobilize">
                            {a.physical_demobilize_at
                              ? dayjs(a.physical_demobilize_at).format('YYYY-MM-DD HH:mm')
                              : '—'}
                          </Descriptions.Item>
                        </Descriptions>
                        <Paragraph type="secondary" style={{ marginTop: 8 }}>
                          LOCATION ≠ HIRE STATUS. Charging stops at commercial off-hire effective —
                          not GPS presence.
                        </Paragraph>
                      </>
                    ) : null}
                  </Card>
                ),
              },
              {
                key: 'responsibility',
                label: 'Responsibility matrix',
                children: (
                  <Card size="small">
                    <Paragraph type="secondary" style={{ marginTop: 0 }}>
                      Explicit parties — avoid free-text-only commercial responsibility.
                    </Paragraph>
                    <Row gutter={16}>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item name="operator_responsibility" label="Operator">
                          <Select options={RESP_PARTY} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item name="fuel_responsibility" label="Fuel">
                          <Select options={RESP_PARTY.filter((r) => r.value !== 'OTHER')} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item name="maintenance_responsibility" label="Maintenance">
                          <Select options={RESP_PARTY.filter((r) => r.value !== 'OTHER')} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item name="tyre_responsibility" label="Tyres">
                          <Select options={RESP_PARTY.filter((r) => r.value !== 'OTHER')} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item name="mob_responsibility" label="Mobilization">
                          <Select options={MOB_DEMOB} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item name="demob_responsibility" label="Demobilization">
                          <Select options={MOB_DEMOB} />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item name="notes" label="Commercial notes">
                          <Input.TextArea
                            rows={4}
                            placeholder="Minimum guarantee conditions, overtime rules summary, weather exclusions, handover notes…"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ),
              },
              ...(isDetail
                ? [
                    {
                      key: 'rates',
                      label: 'Rate schedule',
                      children: (
                        <Card
                          size="small"
                          extra={
                            <Button type="primary" size="small" onClick={() => setRateOpen(true)}>
                              Add effective-dated rate
                            </Button>
                          }
                        >
                          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
                            <Descriptions.Item label="Current effective rate">
                              {currentRateDisplay}
                            </Descriptions.Item>
                            <Descriptions.Item label="Basis">
                              {effectiveRate?.rate_basis || '—'}
                            </Descriptions.Item>
                          </Descriptions>
                          <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Rate changes never rewrite certified hire statements. Add a new effective-dated row."
                          />
                          <Table
                            rowKey="id"
                            size="small"
                            pagination={false}
                            loading={ratesQ.isLoading}
                            dataSource={ratesQ.data ?? []}
                            columns={[
                              { title: 'Code', dataIndex: 'rate_code', width: 100 },
                              { title: 'Label', dataIndex: 'label', ellipsis: true },
                              { title: 'Basis', dataIndex: 'rate_basis', width: 90 },
                              {
                                title: 'Hourly',
                                dataIndex: 'hourly_rate',
                                render: (v, r: PmvHireRate) => formatHireMoney(v, r.currency),
                              },
                              {
                                title: 'Daily',
                                dataIndex: 'daily_rate',
                                render: (v, r: PmvHireRate) => formatHireMoney(v, r.currency),
                              },
                              {
                                title: 'Monthly',
                                dataIndex: 'monthly_rate',
                                render: (v, r: PmvHireRate) => formatHireMoney(v, r.currency),
                              },
                              {
                                title: 'Min h/day',
                                dataIndex: 'daily_minimum_hours',
                                width: 90,
                                render: (v) => (v == null || v === '' ? '—' : String(v)),
                              },
                              {
                                title: 'Standby %',
                                dataIndex: 'standby_pct',
                                width: 90,
                                render: (v) => (v == null || v === '' ? '—' : String(v)),
                              },
                              {
                                title: 'BD %',
                                dataIndex: 'breakdown_pct',
                                width: 70,
                                render: (v) => (v == null || v === '' ? '—' : String(v)),
                              },
                              { title: 'From', dataIndex: 'effective_from', width: 110 },
                              {
                                title: 'Until',
                                dataIndex: 'effective_until',
                                width: 110,
                                render: (v) => v || '—',
                              },
                              {
                                title: 'Active',
                                dataIndex: 'is_active',
                                width: 70,
                                render: (v) => (v === false ? <Tag>No</Tag> : <Tag color="green">Yes</Tag>),
                              },
                            ]}
                          />
                        </Card>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={createMut.isPending || patchMut.isPending}
            >
              {isNew ? 'Create agreement' : 'Save agreement'}
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire/agreements')}>Cancel</Button>
          </Space>
        </Form>

        <Modal
          title="Add effective-dated rate"
          open={rateOpen}
          width={720}
          onCancel={() => setRateOpen(false)}
          onOk={() => rateForm.submit()}
          confirmLoading={rateMut.isPending}
          destroyOnClose
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Backend is money authority. Frontend only captures terms for calculation."
          />
          <Form
            form={rateForm}
            layout="vertical"
            initialValues={{
              rate_basis: 'HOURLY',
              currency: a?.currency || 'AED',
              standby_pct: 50,
              breakdown_pct: 0,
              daily_minimum_hours: 10,
              effective_from: dayjs(),
            }}
            onFinish={(v) =>
              rateMut.mutate({
                ...v,
                effective_from: dayjs(v.effective_from).format('YYYY-MM-DD'),
                effective_until: v.effective_until
                  ? dayjs(v.effective_until).format('YYYY-MM-DD')
                  : null,
              })
            }
          >
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="rate_code" label="Rate code" rules={[{ required: true }]}>
                  <Input placeholder="STD-HRLY" />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item name="label" label="Label" rules={[{ required: true }]}>
                  <Input placeholder="Standard hourly hire" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="rate_basis" label="Basis" rules={[{ required: true }]}>
                  <Select options={RATE_BASIS} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="currency" label="Currency">
                  <Select options={currencyOptions} showSearch optionFilterProp="label" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="hourly_rate" label="Hourly rate">
                  <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="daily_rate" label="Daily rate">
                  <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="monthly_rate" label="Monthly rate">
                  <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="daily_minimum_hours"
                  label="Minimum hours / day"
                  extra="Applied per contract rules (not blindly on vendor breakdown)."
                >
                  <InputNumber style={{ width: '100%' }} min={0} step={0.25} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="standby_pct" label="Standby % of rate">
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={1} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="breakdown_pct" label="Breakdown % of rate">
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={1} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="idle_pct" label="Idle % of rate">
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={1} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="overtime_multiplier" label="Overtime multiplier">
                  <InputNumber style={{ width: '100%' }} min={1} step={0.1} placeholder="e.g. 1.5" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="monthly_proration_days" label="Monthly proration days">
                  <InputNumber style={{ width: '100%' }} min={1} max={31} step={1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="effective_until" label="Effective until">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="notes" label="Rate notes">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
            <Collapse
              ghost
              items={[
                {
                  key: 'hint',
                  label: 'EXC-H-021 example',
                  children: (
                    <Text type="secondary">
                      Hourly AED 185, minimum 10 h/day, standby 50%, vendor breakdown 0% → Day with
                      9.5h working charges AED 1,850; mixed standby/breakdown day charges working +
                      standby% only.
                    </Text>
                  ),
                },
              ]}
            />
          </Form>
        </Modal>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Hire Agreements"
        subtitle={<Text type="secondary">Rental / lease commercial profiles linked to Asset + PMV</Text>}
        primaryActions={
          <Space>
            <Button type="primary" onClick={() => navigate('/asset/pmv/hire/agreements/new')}>
              New agreement
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 200 }}
            value={status}
            onChange={setStatus}
            options={[
              'PLANNED',
              'CONFIRMED',
              'MOBILIZING',
              'ON_HIRE',
              'OFF_HIRE_REQUESTED',
              'OFF_HIRE_EFFECTIVE',
              'DEMOBILIZING',
              'RETURNED',
              'CLOSED',
              'CANCELLED',
            ].map((v) => ({ value: v, label: v }))}
          />
        }
      />
      <Table
        rowKey="id"
        size="small"
        loading={listQ.isLoading}
        dataSource={listQ.data?.items ?? []}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 1100 }}
        tableLayout="fixed"
        columns={[
          {
            title: 'Agreement',
            dataIndex: 'agreement_number',
            width: 180,
            ellipsis: true,
            render: (v: string, r: PmvHireAgreement) => (
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Button
                  type="link"
                  style={{ padding: 0, height: 'auto', maxWidth: '100%' }}
                  onClick={() => navigate(`/asset/pmv/hire/agreements/${r.id}`)}
                >
                  {v}
                </Button>
              </div>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'commercial_status',
            width: 160,
            render: (s) => <Tag>{s}</Tag>,
          },
          {
            title: 'Supplier',
            dataIndex: 'supplier_id',
            width: 200,
            ellipsis: true,
            render: (sid: string) => supplierLabelById.get(sid) || sid?.slice(0, 8) || '—',
          },
          {
            title: 'Project',
            dataIndex: 'project_id',
            width: 180,
            ellipsis: true,
            render: (pid: string | null) =>
              pid ? projectLabelById.get(pid) || pid.slice(0, 8) : '—',
          },
          { title: 'Ownership', dataIndex: 'ownership_type', width: 120 },
          { title: 'Currency', dataIndex: 'currency', width: 80 },
          {
            title: 'Contract ref',
            dataIndex: 'contract_reference',
            width: 140,
            ellipsis: true,
            render: (v) => v || '—',
          },
          {
            title: 'Equipment',
            dataIndex: 'pmv_profile_id',
            width: 100,
            render: (pid: string) => (
              <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => navigate(`/asset/pmv/${pid}?tab=hire`)}
              >
                Passport
              </Button>
            ),
          },
        ]}
      />
    </WorkspaceLayout>
  );
};

export default PmvHireAgreementPage;
